import { Component, OnInit, OnDestroy, inject, ChangeDetectorRef } from '@angular/core';
import {
  Router,
  RouterLink,
  RouterLinkActive,
  RouterOutlet,
  NavigationEnd,
} from '@angular/router';
import { MatButton } from '@angular/material/button';
import { TranslatePipe } from '@ngx-translate/core';
import { NgForOf, NgIf, NgOptimizedImage } from '@angular/common';
import { MatFormField, MatOption, MatSelect, MatSelectTrigger } from '@angular/material/select';
import { TranslateService } from '@ngx-translate/core';
import { ReserveHealthService } from '../../shared/services/reserve-health.service';
import { SharedStorageService } from '../../shared/services/shared-storage.service';
import { QuestionnaireStatus } from '../../shared/interfaces/reserve-health.model';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

interface OptionItem {
  label: string;
  route: string;
  status?: string;
}

/**
 * 會員中心底版組件，顯示會員中心頁籤
 */
@Component({
  selector: 'app-member',
  standalone: true,
  imports: [
    RouterOutlet,
    MatButton,
    TranslatePipe,
    RouterLink,
    NgOptimizedImage,
    RouterLinkActive,
    MatFormField,
    MatSelect,
    MatOption,
    NgForOf,
    NgIf,
    MatSelectTrigger,
  ],
  templateUrl: './member.component.html',
  styleUrl: './member.component.scss',
})
export class MemberComponent implements OnInit, OnDestroy {

  private reserveHealthService: ReserveHealthService = inject(ReserveHealthService);
  private sharedStorageService: SharedStorageService = inject(SharedStorageService);
  private cdr: ChangeDetectorRef = inject(ChangeDetectorRef);
  private subscription: Subscription = new Subscription();

  options: OptionItem[] = [];
  selectedRoute = '';

  // 問卷狀態 - 初始設為 WAITING 避免閃爍
  questionnaireStatus: QuestionnaireStatus = 'WAITING';

  // 控制標籤顯示 - 初始設為 false，避免閃爍
  showUnfinishedTag: boolean = false;

  // 初始化完成標記 - 避免初始化過程中的閃爍
  private isInitialized: boolean = false;

  // myReservation 載入狀態 - 控制健調表顯示時機
  isMyReservationLoaded: boolean = false;

  constructor(
    private router: Router,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    console.log('Member component: ngOnInit 開始');

    // 獲取初始狀態
    this.questionnaireStatus = this.reserveHealthService.getCurrentQuestionnaireStatus();
    console.log('Member component: 取得初始狀態:', this.questionnaireStatus);

    // 標記初始化完成
    this.isInitialized = true;
    console.log('Member component: 初始化完成');

    // 初始化完成後才進行 tag 顯示判斷
    this.updateUnfinishedTagVisibility();

    // 監聽 myReservation 載入事件，當載入完成時立即檢查問卷狀態
    this.monitorMyReservationLoad();

    // 延遲觸發問卷狀態檢查（作為備用機制）
    setTimeout(() => {
      this.triggerQuestionnaireStatusCheck();
    }, 1000);

    // 訂閱狀態變化
    this.subscription.add(
      this.reserveHealthService.questionnaireStatus$.subscribe(status => {
        console.log('Member component: 收到狀態變化通知:', status, '初始化狀態:', this.isInitialized);
        this.questionnaireStatus = status;
        this.updateUnfinishedTagVisibility();
      })
    );

    // 訂閱路由變化 - 只記錄，不重新計算狀態
    // 狀態變化應該由各個頁面的 component 負責更新到 service
    this.subscription.add(
      this.router.events.pipe(
        filter(event => event instanceof NavigationEnd)
      ).subscribe((event) => {
        console.log('Member component: 路由變化到', (event as NavigationEnd).url);
        // 不重新計算狀態，只依賴 service 的狀態變化通知
      })
    );

    // 載入翻譯並初始化選項
    this.loadTranslationsAndInitOptions();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  /**
   * 檢查是否有有效的預約資料
   */
  hasValidReservation(): boolean {
    const myReservation = this.sharedStorageService.get('myReservation');
    return !!(myReservation?.caseNo);
  }

  /**
   * 觸發問卷狀態檢查（通知 ReserveHealthService 進行檢查）
   */
  private triggerQuestionnaireStatusCheck(): void {
    console.log('Member component: 觸發問卷狀態檢查');

    // 檢查是否已經有完整的問卷資料
    const hasCompleteData = this.checkIfHasCompleteQuestionnaireData();
    if (hasCompleteData) {
      console.log('Member component: 已有完整問卷資料，不需要觸發檢查');
      // 確保狀態是 COMPLETED
      this.reserveHealthService.updateQuestionnaireStatus('COMPLETED');
      return;
    }

    // 通知 ReserveHealthService 進行狀態檢查
    // 只有當前狀態是 WAITING 時才進行檢查，避免覆蓋已經正確的狀態
    const currentStatus = this.reserveHealthService.getCurrentQuestionnaireStatus();

    if (currentStatus === 'WAITING') {
      console.log('Member component: 當前狀態是 WAITING，但先檢查是否有 CB-043 API 正在執行');

      // 檢查是否有 myReservation 資料，如果有的話 CB-043 API 可能正在執行
      const myReservation = this.sharedStorageService.get('myReservation');
      if (myReservation?.caseNo) {
        console.log('Member component: 有 myReservation 資料，CB-043 API 可能正在執行，跳過延遲檢查');
        return;
      }

      console.log('Member component: 沒有 myReservation 資料，觸發 ReserveHealthService 檢查');
      this.reserveHealthService.checkAndUpdateQuestionnaireStatus();
    } else {
      console.log('Member component: 當前狀態不是 WAITING，不觸發檢查，當前狀態:', currentStatus);
    }
  }

  /**
   * 檢查是否有完整的問卷資料（簡化版本，只檢查關鍵條件）
   */
  private checkIfHasCompleteQuestionnaireData(): boolean {
    try {
      const savedData = this.sharedStorageService.get('savedQuestionnaireData');
      const myReservation = this.sharedStorageService.get('myReservation');

      // 基本檢查
      if (!savedData?.questions || !savedData.savedAt || !myReservation?.caseNo) {
        return false;
      }

      // 檢查 caseNo 是否匹配
      if (savedData.caseNo !== myReservation.caseNo) {
        return false;
      }

      // 檢查問題數量（簡化判斷）
      const questionCount = savedData.questions.length;
      const hasEnoughQuestions = questionCount >= 30;

      console.log('Member component: 簡化完整性檢查:', {
        questionCount,
        hasEnoughQuestions,
        hasSavedAt: !!savedData.savedAt,
        caseNoMatch: savedData.caseNo === myReservation.caseNo
      });

      return hasEnoughQuestions;
    } catch (error) {
      console.error('Member component: 檢查完整問卷資料失敗:', error);
      return false;
    }
  }

  /**
   * 解析預約日期
   */
  private parseBookDate(bookDateStr: string): Date {
    // bookDateStr 格式: "20251103"
    const year = parseInt(bookDateStr.substring(0, 4));
    const month = parseInt(bookDateStr.substring(4, 6)) - 1; // 月份從0開始
    const day = parseInt(bookDateStr.substring(6, 8));
    return new Date(year, month, day);
  }

  /**
   * 檢查問卷是否完整
   */
  private checkIfQuestionnaireIsComplete(): boolean {
    try {
      const savedData = this.sharedStorageService.get('savedQuestionnaireData');

      if (!savedData?.questions || !Array.isArray(savedData.questions)) {
        return false;
      }

      // 檢查 caseNo 是否匹配
      const myReservation = this.sharedStorageService.get('myReservation');
      if (savedData.caseNo !== myReservation?.caseNo) {
        return false;
      }

      // 檢查是否有 savedAt
      if (!savedData.savedAt) {
        return false;
      }

      const questions = savedData.questions;
      const questionCount = questions.length;

      // 檢查關鍵頁面是否存在
      const hasPersonalHistory = questions.some((q: any) => q.questionId === '474');
      const hasFamilyHistory = questions.some((q: any) => q.questionId === '483');
      const hasSubjectiveSymptoms = questions.some((q: any) => q.questionId === '472');
      const hasBasicInfo = questions.some((q: any) =>
        ['511', '513', '514', '515'].includes(q.questionId)
      );
      const hasWorkExperience = questions.some((q: any) =>
        ['519', '523', '525'].includes(q.questionId)
      );

      // 完整問卷的判斷條件
      const hasEnoughQuestions = questionCount >= 30;
      const hasCorePages = hasPersonalHistory && hasFamilyHistory && hasSubjectiveSymptoms;
      const hasEssentialPages = hasBasicInfo || hasWorkExperience;

      const isComplete = hasEnoughQuestions && hasCorePages && hasEssentialPages;

      console.log('Member component: 問卷完整性檢查:', {
        questionCount,
        hasPersonalHistory,
        hasFamilyHistory,
        hasSubjectiveSymptoms,
        hasBasicInfo,
        hasWorkExperience,
        hasEnoughQuestions,
        hasCorePages,
        hasEssentialPages,
        isComplete
      });

      return isComplete;
    } catch (error) {
      console.error('Member component: 檢查問卷完整性失敗:', error);
      return false;
    }
  }

  /**
   * 監聽 myReservation 載入事件
   */
  private monitorMyReservationLoad(): void {
    console.log('Member component: 開始監聽 myReservation 載入');

    // 先檢查是否已經載入
    const existingReservation = this.sharedStorageService.get('myReservation');
    if (existingReservation?.caseNo) {
      console.log('Member component: myReservation 已存在，立即檢查:', existingReservation);
      this.isMyReservationLoaded = true;
      this.reserveHealthService.initializeQuestionnaireStatusFromAPI();
      return;
    }

    // 使用 setInterval 定期檢查 myReservation 是否已載入
    const checkInterval = setInterval(() => {
      const myReservation = this.sharedStorageService.get('myReservation');

      if (myReservation?.caseNo) {
        console.log('Member component: 檢測到 myReservation 載入完成:', myReservation);
        clearInterval(checkInterval);

        // 設定載入完成狀態
        this.isMyReservationLoaded = true;

        // 立即調用 API 檢查問卷狀態
        this.reserveHealthService.initializeQuestionnaireStatusFromAPI();
      }
    }, 200); // 每200ms檢查一次，降低頻率

    // 設定超時，避免無限等待
    setTimeout(() => {
      clearInterval(checkInterval);
      console.log('Member component: myReservation 監聽超時，停止監聽');

      // 超時後檢查是否真的沒有預約資料
      const finalCheck = this.sharedStorageService.get('myReservation');
      if (!finalCheck?.caseNo) {
        console.log('Member component: 確認沒有預約資料，設定載入完成但不顯示健調表');
        this.isMyReservationLoaded = true; // 設定為已載入，但因為沒有預約資料，健調表邏輯會處理
      }
    }, 3000); // 3秒後停止監聽
  }

  /**
   * 預先檢查並更新問卷狀態，避免第一次進入時閃爍
   * 直接調用 API 檢查，不依賴 SharedStorage
   */
  private preCheckAndUpdateQuestionnaireStatus(): void {
    try {
      console.log('Member component: 開始預先檢查問卷狀態（調用 API）');

      // 檢查是否有 myReservation 資料
      const myReservation = this.sharedStorageService.get('myReservation');
      console.log('Member component: 檢查 myReservation:', myReservation);

      if (!myReservation?.caseNo) {
        console.log('Member component: 沒有 myReservation 資料，跳過 API 檢查');
        return;
      }

      // 直接調用 ReserveHealthService 的 API 檢查方法
      this.reserveHealthService.initializeQuestionnaireStatusFromAPI();
    } catch (error) {
      console.error('Member component: 預先檢查問卷狀態失敗:', error);
    }
  }

  /**
   * 載入翻譯並初始化選項
   */
  private loadTranslationsAndInitOptions(): void {
    this.translate
      .get([
        'member.my-reservation',
        'member.health-questionnaire',
        'member.unfinished',
        'member.query-exam-report',
        'member.modify-basic-info',
      ])
      .subscribe(translations => {
        console.log('Member component: 載入翻譯完成，showUnfinishedTag:', this.showUnfinishedTag);

        this.options = [
          {
            label: translations['member.my-reservation'],
            route: '/healthcheck/member/mybooking',
          },
          // {
          //   label: translations['member.health-questionnaire'],
          //   route: '/healthcheck/member/form',
          //   // 強制設為 undefined，避免翻譯載入時機造成的閃爍
          //   status: undefined,
          // },
          // {
          //   label: translations['member.query-exam-report'],
          //   route: '/healthcheck/member/report',
          // },
          {
            label: translations['member.modify-basic-info'],
            route: '/healthcheck/member/editprofile',
          },
        ];

        const currentUrl = this.router.url;
        const matchedOption = this.options.find(option =>
          currentUrl.startsWith(option.route)
        );
        this.selectedRoute = matchedOption?.route ?? '';

        // 翻譯載入完成後，立即更新狀態
        this.updateOptionsStatus();
      });
  }

  /**
   * 更新「未完成」標籤的顯示狀態
   * 直接使用 service 的邏輯判斷
   */
  private updateUnfinishedTagVisibility(): void {
    console.log('Member component: updateUnfinishedTagVisibility 被調用', {
      isInitialized: this.isInitialized,
      currentStatus: this.questionnaireStatus,
      currentShowTag: this.showUnfinishedTag
    });

    // 在初始化完成前，強制保持 false 避免閃爍
    if (!this.isInitialized) {
      console.log('Member component: 初始化未完成，強制 showUnfinishedTag = false');
      this.showUnfinishedTag = false;
      return;
    }

    // 直接使用 service 提供的方法
    const newShowStatus = this.reserveHealthService.shouldShowUnfinishedTag();

    console.log('Member component: updateUnfinishedTagVisibility', {
      currentStatus: this.questionnaireStatus,
      currentShowTag: this.showUnfinishedTag,
      newShowStatus,
      willUpdate: this.showUnfinishedTag !== newShowStatus,
      isInitialized: this.isInitialized
    });

    if (this.showUnfinishedTag !== newShowStatus) {
      // 使用 setTimeout 避免 Angular 變更檢測錯誤
      setTimeout(() => {
        console.log('Member component: 即將更新 showUnfinishedTag 從', this.showUnfinishedTag, '到', newShowStatus);
        this.showUnfinishedTag = newShowStatus;
        this.updateOptionsStatus();
        console.log('Member component: 更新 showUnfinishedTag 為', newShowStatus);
      }, 0);
    } else {
      console.log('Member component: showUnfinishedTag 無需更新，維持', this.showUnfinishedTag);
    }
  }

  /**
   * 更新選項中的狀態標籤
   */
  private updateOptionsStatus(): void {
    if (this.options.length === 0) {
      console.log('Member component: updateOptionsStatus - options 陣列為空，跳過更新');
      return;
    }

    this.translate.get('member.unfinished').subscribe(unfinishedText => {
      const healthQuestionnaireOption = this.options.find(
        option => option.route === '/healthcheck/member/form'
      );

      if (healthQuestionnaireOption) {
        const oldStatus = healthQuestionnaireOption.status;
        healthQuestionnaireOption.status = this.showUnfinishedTag
          ? unfinishedText
          : undefined;

        console.log('Member component: updateOptionsStatus', {
          showUnfinishedTag: this.showUnfinishedTag,
          oldStatus,
          newStatus: healthQuestionnaireOption.status,
          unfinishedText
        });
      }
    });
  }

  onRouteChange(route: string): void {
    this.selectedRoute = route;
    this.router.navigate([route]);
  }

  getSelectedLabel(): string {
    const selected = this.options.find(opt => opt.route === this.selectedRoute);
    return selected ? selected.label : '';
  }

  getSelectedStatus(): string {
    const selected = this.options.find(opt => opt.route === this.selectedRoute);
    return selected?.status || '';
  }
}